import {
  alpha,
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import { memo, useCallback, useEffect, useState } from 'react'
import { IoChevronDown } from 'react-icons/io5'

import { Alert, SidebarSectionList } from '@/types'

import config from './config'
import SidebarItem from './SidebarItem'

const SIDEBAR_STORAGE_KEY = 'sidebar-sections-state'

interface SidebarItemsProps {
  token: string
  collapsed: boolean
  alerts: Alert[]
}

function SidebarItems({ token, alerts, collapsed }: SidebarItemsProps) {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const theme = useTheme()

  useEffect(() => {
    try {
      const savedState = localStorage.getItem(SIDEBAR_STORAGE_KEY)

      if (savedState) {
        const parsedState = JSON.parse(savedState)
        setOpenSections(parsedState)
      } else {
        const initialState: Record<string, boolean> = {}
        config.forEach(({ header }) => {
          initialState[header] = true
        })
        setOpenSections(initialState)
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(initialState))
      }
    } catch (err) {
      const fallbackState: Record<string, boolean> = {}
      config.forEach(({ header }) => {
        fallbackState[header] = true
      })
      setOpenSections(fallbackState)
      console.error(err) // eslint-disable-line
    }
  }, [])

  useEffect(() => {
    if (Object.keys(openSections).length > 0) {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(openSections))
    }
  }, [openSections])

  const toggleSection = useCallback((sectionHeader: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionHeader]: !prev[sectionHeader],
    }))
  }, [])

  if (collapsed) {
    return (
      <List disablePadding>
        {config.map(({ items }) =>
          items.map((item) => (
            <SidebarItem
              key={item.title}
              {...item}
              token={token}
              collapsed={collapsed}
              alerts={alerts}
            />
          )),
        )}
      </List>
    )
  }

  return (
    <>
      {config.map(({ header, items }: SidebarSectionList) => {
        const isOpen = openSections[header] || false

        return (
          <Box key={header} sx={{ mb: 2 }}>
            <ListItemButton
              onClick={() => toggleSection(header)}
              sx={{
                borderRadius: 2,
                mb: 1,
                py: 1.5,
                px: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                border: 1,
                borderColor: alpha(theme.palette.primary.main, 0.08),
                transition: theme.transitions.create(['all'], {
                  duration: theme.transitions.duration.short,
                }),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: alpha(theme.palette.primary.main, 0.16),
                  transform: 'translateX(4px)',
                  boxShadow: theme.shadows[2],
                },
              }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box
                  sx={{
                    transition: theme.transitions.create(['transform'], {
                      duration: theme.transitions.duration.short,
                    }),
                    transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}>
                  <IoChevronDown size={16} color={theme.palette.text.secondary} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '0.75rem',
                    }}>
                    {header}
                  </Typography>
                }
              />
            </ListItemButton>

            <Collapse in={isOpen} timeout={300} unmountOnExit>
              <List disablePadding sx={{ pl: 0.5 }}>
                {items.map((item) => (
                  <SidebarItem
                    key={item.title}
                    {...item}
                    token={token}
                    collapsed={false}
                    alerts={alerts}
                  />
                ))}
              </List>
            </Collapse>
          </Box>
        )
      })}
    </>
  )
}

export default memo(SidebarItems)
